import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch, useAppDispatch } from '../support/store';
import {
  fetchPriorities,
  fetchChallenges,
  fetchFrameworks,
  fetchOutcomes,
  fetchArchitectures,
  setPriorities,
  setChallenges,
  setFrameworks,
  setOutcomes,
  setArchitectures,
  fetchSentimentOptions,
  fetchApprovalStatus,
} from '../support/slices/toolboxSlice';

// ------ PRIORITIES -------
export const usePriorities = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const priorities = useSelector((state: RootState) => state.toolbox.priorities);
  const lookup = useSelector((state: RootState) => state.toolbox.prioritiesLookup);
  const loading = useSelector((state: RootState) => state.toolbox.prioritiesLoading);
  const error = useSelector((state: RootState) => state.toolbox.prioritiesError);

  useEffect(() => {
    if (autoFetch && priorities.length === 0 && !loading) {
      dispatch(fetchPriorities());
    }
  }, [autoFetch, priorities.length, dispatch, loading]);

  const set = (data: any) => dispatch(setPriorities(data));

  return { priorities, lookup, loading, error, refetch: () => dispatch(fetchPriorities()), set };
};

// ------ CHALLENGES -------
export const useChallenges = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const challenges = useSelector((state: RootState) => state.toolbox.challenges);
  const lookup = useSelector((state: RootState) => state.toolbox.challengesLookup);
  const loading = useSelector((state: RootState) => state.toolbox.challengesLoading);
  const error = useSelector((state: RootState) => state.toolbox.challengesError);

  useEffect(() => {
    if (autoFetch && challenges.length === 0 && !loading) {
      dispatch(fetchChallenges());
    }
  }, [autoFetch, challenges.length, dispatch, loading]);

  const set = (data: any) => dispatch(setChallenges(data));

  return { challenges, lookup, loading, error, refetch: () => dispatch(fetchChallenges()), set };
};

// ------ FRAMEWORKS -------
export const useFrameworks = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const frameworks = useSelector((state: RootState) => state.toolbox.frameworks);
  const lookup = useSelector((state: RootState) => state.toolbox.frameworksLookup);
  const loading = useSelector((state: RootState) => state.toolbox.frameworksLoading);
  const error = useSelector((state: RootState) => state.toolbox.frameworksError);

  useEffect(() => {
    if (autoFetch && frameworks.length === 0 && !loading) {
      dispatch(fetchFrameworks());
    }
  }, [autoFetch, frameworks.length, dispatch, loading]);

  const set = (data: any) => dispatch(setFrameworks(data));

  return { frameworks, lookup, loading, error, refetch: () => dispatch(fetchFrameworks()), set };
};

export const useSentimentOptions = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const sentimentOptions = useSelector((state: RootState) => state.toolbox.sentimentOptions);
  const loading = useSelector((state: RootState) => state.toolbox.sentimentOptionsLoading);
  const error = useSelector((state: RootState) => state.toolbox.sentimentOptionsError);

  useEffect(() => {
    if (autoFetch && sentimentOptions.length === 0 && !loading) {
      dispatch(fetchSentimentOptions());
    }
  }, [autoFetch, sentimentOptions.length, dispatch, loading]);

  return { sentimentOptions, loading, error, refetch: () => dispatch(fetchSentimentOptions()) };
};

// ------ OUTCOMES -------
export const useOutcomes = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const outcomes = useSelector((state: RootState) => state.toolbox.outcomes);
  const loading = useSelector((state: RootState) => state.toolbox.outcomesLoading);
  const error = useSelector((state: RootState) => state.toolbox.outcomesError);

  useEffect(() => {
    if (autoFetch && outcomes.length === 0 && !loading) {
      dispatch(fetchOutcomes());
    }
  }, [autoFetch, outcomes.length, dispatch, loading]);

  const set = (data: any) => dispatch(setOutcomes(data));

  return { outcomes, loading, error, refetch: () => dispatch(fetchOutcomes()), set };
};

// ------ ARCHITECTURES -------
export const useArchitectures = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const architectures = useSelector((state: RootState) => state.toolbox.architectures);
  const loading = useSelector((state: RootState) => state.toolbox.architecturesLoading);
  const error = useSelector((state: RootState) => state.toolbox.architecturesError);

  useEffect(() => {
    if (autoFetch && architectures.length === 0 && !loading) {
      dispatch(fetchArchitectures());
    }
  }, [autoFetch, architectures.length, dispatch, loading]);

  const set = (data: any) => dispatch(setArchitectures(data));

  return { architectures, loading, error, refetch: () => dispatch(fetchArchitectures()), set };
};

export const useApprovalStatus = () => {
  const dispatch = useAppDispatch();
  const { approvalStatus, approvalStatusLoading, approvalStatusError } = useSelector(
    (state: RootState) => state.toolbox
  );

  useEffect(() => {
      dispatch(fetchApprovalStatus());
  }, [dispatch]);

  return { approvalStatus, approvalStatusLoading, approvalStatusError };
};